<template>
  <div id="message-detail" class="page-shell" v-loading="loading">
    <el-breadcrumb separator-class="el-icon-arrow-right" class="page-breadcrumb">
      <el-breadcrumb-item :to="{ name: 'Dashboard' }">Dashboard</el-breadcrumb-item>
      <el-breadcrumb-item :to="{ name: parentRouteName }">{{ parentLabel }}</el-breadcrumb-item>
      <el-breadcrumb-item>Message Detail</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="page-header message-detail__header">
      <div>
        <h1 class="page-title">Message Detail</h1>
        <p class="message-detail__subtitle">
          {{ detail ? (detail.fileName || detail.messageId) : (activeMessageId || 'Review the tracked AS2 message record.') }}
        </p>
      </div>

      <el-button plain icon="el-icon-back" @click="goBack">Back</el-button>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      :closable="false"
      class="message-detail__alert"
    />

    <template v-else-if="detail">
      <el-card class="panel-card message-detail__summary" shadow="never">
        <div class="message-detail__summary-top">
          <div class="message-detail__summary-badges">
            <span :class="getStatusClass(detail.overallStatusLabel)" class="message-detail__status-pill">
              {{ detail.overallStatusLabel }}
            </span>
            <el-tag :type="detail.direction === 'Receive' ? 'info' : 'warning'" effect="plain">
              {{ detail.direction }}
            </el-tag>
          </div>

          <div class="message-detail__route-copy">{{ detail.senderId || '-' }} -> {{ detail.receiverId || '-' }}</div>
        </div>

        <p class="message-detail__state-message">{{ detail.overallStateMessage || 'No state message available.' }}</p>
        <p class="message-detail__state-code">{{ detail.overallState || '-' }}</p>
        <p class="message-detail__message-id">{{ detail.messageId || '-' }}</p>
      </el-card>

      <div class="message-detail__grid">
        <el-card class="panel-card" shadow="never">
          <div slot="header" class="message-detail__section-title">Overview</div>
          <dl class="message-detail__meta-list">
            <div v-for="item in overviewItems" :key="item.label" class="message-detail__meta-item">
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>
        </el-card>

        <el-card class="panel-card" shadow="never">
          <div slot="header" class="message-detail__section-title">Identifiers</div>
          <dl class="message-detail__meta-list">
            <div v-for="item in identifierItems" :key="item.label" class="message-detail__meta-item">
              <dt>{{ item.label }}</dt>
              <dd class="message-detail__meta-item-value--wrap">{{ item.value }}</dd>
            </div>
          </dl>
        </el-card>

        <el-card class="panel-card" shadow="never">
          <div slot="header" class="message-detail__section-title">Security</div>
          <dl class="message-detail__meta-list">
            <div v-for="item in securityItems" :key="item.label" class="message-detail__meta-item">
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>
        </el-card>

        <el-card class="panel-card" shadow="never">
          <div slot="header" class="message-detail__section-title">Delivery</div>
          <dl class="message-detail__meta-list">
            <div v-for="item in deliveryItems" :key="item.label" class="message-detail__meta-item">
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>
        </el-card>
      </div>

      <el-card class="panel-card message-detail__full-width" shadow="never">
        <div slot="header" class="message-detail__section-heading">
          <div>
            <div class="message-detail__section-title">Retry Effort</div>
            <p class="message-detail__section-subtitle">
              The root transaction stays visible in the table. Any automatic resend attempts are tracked here.
            </p>
          </div>

          <el-tag
            :type="getStatusType(detail.overallStatusLabel)"
            effect="plain"
          >
            {{ detail.overallStatusLabel }}
          </el-tag>
        </div>

        <template v-if="detail.retrySummary">
          <div class="message-detail__retry-overview">
            <div class="message-detail__retry-overview-copy">
              {{ retryOverviewText }}
            </div>

            <div class="message-detail__chip-list">
              <span v-for="item in retrySummaryItems" :key="item.label" class="message-detail__chip">
                <strong>{{ item.label }}:</strong> {{ item.value }}
              </span>
            </div>
          </div>

          <div v-if="detail.retryRootMessageId && detail.retryRootMessageId !== detail.messageId" class="message-detail__group">
            <div class="message-detail__group-label">Retry Root Message ID</div>
            <div class="message-detail__chip-list">
              <span class="message-detail__chip">{{ detail.retryRootMessageId }}</span>
            </div>
          </div>

          <el-collapse v-if="detail.retryAttempts.length" v-model="activeRetryAttempt" accordion class="message-detail__retry-accordion">
            <el-collapse-item
              v-for="attempt in detail.retryAttempts"
              :key="attempt.messageId"
              :name="attempt.messageId"
            >
              <template slot="title">
                <div class="message-detail__retry-header">
                  <div class="message-detail__retry-header-main">
                    <span class="message-detail__retry-header-label">{{ formatAttemptLabel(attempt.attemptNumber) }}</span>
                    <span class="message-detail__retry-header-time">{{ formatDate(attempt.createdAt) }}</span>
                  </div>
                  <div class="message-detail__retry-meta">
                    <span class="message-detail__retry-state-inline">{{ attempt.stateMessage }}</span>
                    <el-tag size="mini" :type="getStatusType(attempt.statusLabel)" effect="plain">
                      {{ attempt.statusLabel }}
                    </el-tag>
                  </div>
                </div>
              </template>

              <div class="message-detail__retry-entry">
                <p class="message-detail__retry-state-message">{{ attempt.stateMessage }}</p>

                <div class="message-detail__retry-message-id">
                  {{ attempt.messageId }}
                </div>

                <div
                  v-if="attempt.priorMessageId && attempt.priorMessageId !== '-'"
                  class="message-detail__retry-hop"
                >
                  {{ attempt.priorMessageId }} -> {{ attempt.messageId }}
                </div>

                <dl class="message-detail__retry-details">
                  <div>
                    <dt>State</dt>
                    <dd>{{ attempt.state }}</dd>
                  </div>
                  <div>
                    <dt>Workflow</dt>
                    <dd>{{ attempt.workflowStatus }}</dd>
                  </div>
                  <div>
                    <dt>Resend Count</dt>
                    <dd>{{ attempt.resendCount }}</dd>
                  </div>
                </dl>
              </div>
            </el-collapse-item>
          </el-collapse>

          <div v-else class="message-detail__mini-empty">
            No retry attempts were recorded for this transaction.
          </div>
        </template>
      </el-card>

      <el-card v-if="detail.mdnResponse" class="panel-card message-detail__full-width" shadow="never">
        <div slot="header" class="message-detail__section-title">
          {{ detail.retryAttempts.length ? 'Latest MDN Response' : 'MDN Response' }}
        </div>
        <pre class="message-detail__pre">{{ detail.mdnResponse }}</pre>
      </el-card>
    </template>

    <div v-else class="panel-card message-detail__empty">
      <i class="el-icon-document"></i>
      <h3>No message selected</h3>
      <p>Select a message from the transactions or messages list to inspect its detail.</p>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Utils from '../utils'

export default {
  name: 'message-detail',
  data() {
    return {
      loading: false,
      error: '',
      detail: null,
      activeRetryAttempt: '',
      stateMap: {
        success: [
          'msg_send_start',
          'msg_receive_start',
          'mdn_send_start',
          'mdn_receive_start',
          'msg_sent_mdn_received_ok',
          'msg_rxd_mdn_sent_ok',
          'msg_rxd_mdn_not_requested_ok',
          'msg_sent_mdn_received_mic_mismatch',
        ],
        pending: [
          'msg_send_exception',
          'msg_receive_exception',
          'mdn_sending_exception',
          'mdn_receiving_exception',
          'msg_sent_await_async_mdn_response',
          'msg_sent_mdn_processing_error',
        ],
        failed: [
          'msg_send_fail',
          'msg_send_fail_resend_queued',
          'msg_receive_fail',
          'msg_rxd_asyn_mdn_send_fail_resend_queued',
          'mdn_asyn_receive_fail',
          'msg_rxd_mdn_sending_fail',
          'msg_receive_error_sending_mdn_error',
          'msg_sent_mdn_received_error',
        ],
      },
    }
  },
  computed: {
    activeMessageId() {
      const queryMsgId = this.$route.query.msgId
      if (queryMsgId) return queryMsgId

      const routeId = this.$route.params.id
      if (!routeId) return ''

      return this.safeDecode(routeId)
    },
    parentLabel() {
      return this.$route.query.source === 'transactions' ? 'Transactions' : 'Messages'
    },
    parentRouteName() {
      return this.$route.query.source === 'transactions' ? 'Transactions' : 'Messages'
    },
    overviewItems() {
      if (!this.detail) return []

      return [
        { label: 'File Name', value: this.detail.fileName },
        { label: 'Direction', value: this.detail.direction },
        { label: 'Sender AS2 ID', value: this.detail.senderId },
        { label: 'Receiver AS2 ID', value: this.detail.receiverId },
        { label: 'Created At', value: this.formatDate(this.detail.createdAt) },
        { label: 'Updated At', value: this.formatDate(this.detail.updatedAt) },
      ]
    },
    identifierItems() {
      if (!this.detail) return []

      return [
        { label: 'Message ID', value: this.detail.messageId },
        { label: 'MDN ID', value: this.detail.mdnId },
        { label: 'Retry Root ID', value: this.detail.retryRootMessageId },
        { label: 'Prior Message ID', value: this.detail.priorMessageId },
        { label: 'Tracking ID', value: this.detail.id },
      ]
    },
    securityItems() {
      if (!this.detail) return []

      return [
        { label: 'Content Type', value: this.detail.contentType },
        { label: 'Transfer Encoding', value: this.detail.contentTransferEncoding },
        { label: 'Encryption', value: this.detail.encryptionAlgorithm },
        { label: 'Signature', value: this.detail.signatureAlgorithm },
        { label: 'MDN Mode', value: this.detail.mdnMode },
        { label: 'Compression', value: this.detail.compression },
      ]
    },
    deliveryItems() {
      if (!this.detail) return []

      const currentWorkflow = this.detail.retrySummary.latestWorkflowStatus || this.detail.workflowStatus

      return [
        { label: 'Current Status', value: this.detail.overallStatusLabel },
        { label: 'Current Workflow', value: this.formatValue(currentWorkflow) },
        { label: 'Original Status', value: this.detail.statusLabel },
        { label: 'Original State', value: this.detail.state },
        { label: 'Current State', value: this.detail.overallState },
        { label: 'Resend', value: this.detail.isResend },
        { label: 'Resend Count', value: this.detail.resendCount },
      ]
    },
    retrySummaryItems() {
      if (!this.detail || !this.detail.retrySummary) return []

      return [
        { label: 'Current', value: this.detail.overallStatusLabel },
        { label: 'Retries', value: this.formatValue(this.detail.retrySummary.retryCount) },
        { label: 'Attempts', value: this.formatValue(this.detail.retrySummary.totalAttempts) },
        { label: 'Latest', value: this.formatDate(this.detail.retrySummary.latestCreatedAt) },
      ]
    },
    retryOverviewText() {
      if (!this.detail || !this.detail.retrySummary) return ''

      const totalAttempts = Number(this.detail.retrySummary.totalAttempts || 1)
      const retryCount = Number(this.detail.retrySummary.retryCount || 0)
      const latestAttempt = this.formatDate(this.detail.retrySummary.latestCreatedAt)

      return `This chain ran ${totalAttempts} attempt(s), including ${retryCount} automatic retr${retryCount === 1 ? 'y' : 'ies'}. Latest update: ${latestAttempt}.`
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler() {
        this.loadDetail()
      },
    },
  },
  methods: {
    async loadDetail() {
      this.loading = true
      this.error = ''
      this.detail = null
      this.activeRetryAttempt = ''

      try {
        const messageId = await this.resolveMessageId()
        if (!messageId) {
          throw new Error('Missing message identifier for this detail view.')
        }

        const result = await Utils.Crud.getView('messages', messageId)
        if (!result) {
          throw new Error('Message detail was not found.')
        }

        this.detail = this.normalizeDetail(result)
      } catch (error) {
        this.error = error && error.message ? error.message : String(error)
      } finally {
        this.loading = false
      }
    },
    async resolveMessageId() {
      const queryMsgId = this.$route.query.msgId
      if (queryMsgId) return queryMsgId

      const routeId = this.safeDecode(this.$route.params.id || '')
      if (!routeId) return ''
      if (!/^\d+$/.test(routeId)) return routeId

      const list = await Utils.Crud.getList('messages')
      const row = list[Number(routeId)]
      return this.getField(row || {}, 'MSG_ID') || ''
    },
    normalizeDetail(item) {
      const state = this.getField(item, 'STATE')
      const direction = this.normalizeDirection(this.getField(item, 'DIRECTION'), state)
      const retrySummary = this.normalizeRetrySummary(this.getField(item, 'RETRY_SUMMARY'))
      const retryAttempts = this.normalizeArray(this.getField(item, 'RETRY_ATTEMPTS')).map((attempt, index) => {
        return this.normalizeRetryAttempt(attempt, index)
      })
      const latestAttempt = retryAttempts.length ? retryAttempts[retryAttempts.length - 1] : null
      const overallState = retrySummary.latestState || state
      const overallStateMessage = retrySummary.latestStateMessage || this.getField(item, 'STATE_MSG')
      const originalMdnResponse = this.normalizeNullableValue(this.getField(item, 'MDN_RESPONSE'))
      const latestMdnResponse = latestAttempt && latestAttempt.mdnResponse
        ? latestAttempt.mdnResponse
        : originalMdnResponse
      const originalMdnId = this.formatValue(this.getField(item, 'MDN_ID'))
      const latestMdnId = latestAttempt && latestAttempt.mdnId && latestAttempt.mdnId !== '-'
        ? latestAttempt.mdnId
        : originalMdnId

      return {
        id: this.formatValue(this.getField(item, 'ID')),
        state,
        stateMessage: this.formatValue(this.getField(item, 'STATE_MSG')),
        senderId: this.formatValue(this.getField(item, 'SENDER_ID')),
        receiverId: this.formatValue(this.getField(item, 'RECEIVER_ID')),
        fileName: this.formatValue(this.getField(item, 'FILE_NAME')),
        resendCount: this.formatValue(this.getField(item, 'RESEND_COUNT')),
        isResend: this.normalizeResend(this.getField(item, 'IS_RESEND')),
        mdnResponse: latestMdnResponse,
        contentTransferEncoding: this.formatValue(this.getField(item, 'CONTENT_TRANSFER_ENCODING')),
        contentType: this.formatValue(this.getField(item, 'CONTENT_TYPE')),
        mdnId: latestMdnId,
        updatedAt: this.getField(item, 'UPDATE_DT'),
        priorMessageId: this.formatValue(this.getField(item, 'PRIOR_MSG_ID')),
        signatureAlgorithm: this.formatValue(this.getField(item, 'SIGNATURE_ALGORITHM')),
        mdnMode: this.formatValue(this.getField(item, 'MDN_MODE')),
        createdAt: this.getField(item, 'CREATE_DT'),
        encryptionAlgorithm: this.formatValue(this.getField(item, 'ENCRYPTION_ALGORITHM')),
        messageId: this.formatValue(this.getField(item, 'MSG_ID')),
        compression: this.formatValue(this.getField(item, 'COMPRESSION')),
        direction,
        workflowStatus: this.formatValue(this.getField(item, 'STATUS')),
        statusLabel: this.normalizeStatus(state),
        overallState,
        overallStateMessage: this.formatValue(overallStateMessage),
        overallStatusLabel: this.normalizeStatus(overallState),
        retryRootMessageId: this.formatValue(this.getField(item, 'RETRY_ROOT_MSG_ID')),
        retrySummary,
        retryAttempts,
        raw: item,
      }
    },
    normalizeRetrySummary(item) {
      const summary = item || {}

      return {
        totalAttempts: this.getField(summary, 'TOTAL_ATTEMPTS'),
        retryCount: this.getField(summary, 'RETRY_COUNT'),
        latestMessageId: this.formatValue(this.getField(summary, 'LATEST_MSG_ID')),
        latestState: this.getField(summary, 'LATEST_STATE'),
        latestStateMessage: this.normalizeNullableValue(this.getField(summary, 'LATEST_STATE_MSG')),
        latestWorkflowStatus: this.normalizeNullableValue(this.getField(summary, 'LATEST_STATUS')),
        latestCreatedAt: this.getField(summary, 'LATEST_CREATE_DT'),
      }
    },
    normalizeRetryAttempt(item, index) {
      const state = this.getField(item, 'STATE')

      return {
        attemptNumber: index + 2,
        createdAt: this.getField(item, 'CREATE_DT'),
        messageId: this.formatValue(this.getField(item, 'MSG_ID')),
        mdnId: this.formatValue(this.getField(item, 'MDN_ID')),
        mdnResponse: this.normalizeNullableValue(this.getField(item, 'MDN_RESPONSE')),
        priorMessageId: this.formatValue(this.getField(item, 'PRIOR_MSG_ID')),
        state: this.formatValue(state),
        stateMessage: this.formatValue(this.getField(item, 'STATE_MSG')),
        workflowStatus: this.formatValue(this.getField(item, 'STATUS')),
        resendCount: this.formatValue(this.getField(item, 'RESEND_COUNT')),
        statusLabel: this.normalizeStatus(state),
      }
    },
    getField(item, key) {
      if (!item || typeof item !== 'object') return null

      const lowerKey = key.toLowerCase()
      const value = item[key]
      const lowerValue = item[lowerKey]

      if (value !== undefined && value !== null && value !== '') return value
      if (lowerValue !== undefined && lowerValue !== null && lowerValue !== '') return lowerValue
      return null
    },
    normalizeDirection(direction, state) {
      const normalizedDirection = String(direction || '').toUpperCase()
      if (normalizedDirection === 'RECEIVE' || normalizedDirection === 'RECEIVED' || normalizedDirection === 'INBOUND') {
        return 'Receive'
      }
      if (normalizedDirection === 'SEND' || normalizedDirection === 'SENT' || normalizedDirection === 'OUTBOUND') {
        return 'Send'
      }
      if (String(state || '').indexOf('receive') !== -1 || String(state || '').indexOf('rxd') !== -1) {
        return 'Receive'
      }
      return 'Send'
    },
    normalizeStatus(state) {
      if (this.stateMap.success.includes(state)) return 'Success'
      if (this.stateMap.pending.includes(state)) return 'Pending'
      if (this.stateMap.failed.includes(state)) return 'Failed'
      return 'Pending'
    },
    normalizeResend(value) {
      if (value === 'Y') return 'Yes'
      if (value === 'N') return 'No'
      return this.formatValue(value)
    },
    normalizeArray(value) {
      return Array.isArray(value) ? value.filter((item) => item !== null && item !== undefined && item !== '') : []
    },
    normalizeNullableValue(value) {
      return value == null || value === '' ? '' : value
    },
    formatValue(value) {
      return value == null || value === '' ? '-' : value
    },
    formatDate(value) {
      if (!value) return '-'
      const parsedDate = this.parseDate(value)
      return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    parseDate(value) {
      if (!value) return moment.invalid()

      const parsedDate = moment(
        value,
        ['YYYY-MM-DD HH:mm:ss.SSS', 'YYYY-MM-DD HH:mm:ss.SS', 'YYYY-MM-DD HH:mm:ss.S', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601],
        true,
      )

      return parsedDate.isValid() ? parsedDate : moment(value)
    },
    safeDecode(value) {
      try {
        return decodeURIComponent(value)
      } catch (error) {
        return value
      }
    },
    goBack() {
      this.$router.push({ name: this.parentRouteName })
    },
    formatAttemptLabel(attemptNumber) {
      return 'Attempt ' + attemptNumber
    },
    getStatusClass(status) {
      if (status === 'Success') return 'message-detail__status-pill--success'
      if (status === 'Pending') return 'message-detail__status-pill--warning'
      return 'message-detail__status-pill--danger'
    },
    getStatusType(status) {
      if (status === 'Success') return 'success'
      if (status === 'Pending') return 'warning'
      if (status === 'Failed') return 'danger'
      return 'info'
    },
  },
}
</script>

<style scoped>
.message-detail__header {
  margin-bottom: 20px;
}

.message-detail__subtitle {
  margin: 6px 0 0;
  color: #909399;
  word-break: break-word;
}

.message-detail__alert {
  margin-bottom: 20px;
}

.message-detail__summary {
  margin-bottom: 20px;
}

.message-detail__summary-top {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.message-detail__summary-badges {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.message-detail__status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 84px;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.message-detail__status-pill--success {
  background: #67c23a;
  color: #fff;
}

.message-detail__status-pill--warning {
  background: #e6a23c;
  color: #fff;
}

.message-detail__status-pill--danger {
  background: #f56c6c;
  color: #fff;
}

.message-detail__route-copy {
  color: #606266;
  font-weight: 600;
  word-break: break-word;
}

.message-detail__state-message {
  margin: 18px 0 8px;
  color: #303133;
  font-size: 16px;
  line-height: 1.6;
}

.message-detail__state-code,
.message-detail__message-id {
  margin: 0;
  color: #909399;
  word-break: break-word;
}

.message-detail__message-id {
  margin-top: 6px;
}

.message-detail__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.message-detail__full-width {
  margin-bottom: 20px;
}

.message-detail__section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.message-detail__section-title {
  font-weight: 700;
}

.message-detail__section-subtitle {
  margin: 6px 0 0;
  color: #909399;
  font-size: 13px;
}

.message-detail__meta-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0;
}

.message-detail__meta-item {
  display: grid;
  grid-template-columns: minmax(0, 160px) minmax(0, 1fr);
  gap: 16px;
}

.message-detail__meta-item dt {
  margin: 0;
  color: #909399;
  font-weight: 600;
}

.message-detail__meta-item dd {
  margin: 0;
  color: #303133;
  word-break: break-word;
}

.message-detail__meta-item-value--wrap {
  white-space: normal;
}

.message-detail__pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: SFMono-Regular, Consolas, monospace;
  line-height: 1.6;
}

.message-detail__retry-overview {
  margin-bottom: 18px;
}

.message-detail__retry-overview-copy {
  margin-bottom: 12px;
  color: #606266;
  line-height: 1.6;
}

.message-detail__group {
  margin-bottom: 18px;
}

.message-detail__group-label {
  margin-bottom: 10px;
  color: #606266;
  font-weight: 700;
}

.message-detail__group-copy {
  margin: 0 0 10px;
  color: #909399;
  word-break: break-word;
}

.message-detail__chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.message-detail__chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 7px 10px;
  border-radius: 999px;
  background: #f4f4f5;
  color: #303133;
  font-size: 12px;
  line-height: 1.4;
  word-break: break-word;
}

.message-detail__chip strong {
  margin-right: 4px;
}

.message-detail__retry-accordion {
  border-top: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
}

.message-detail__retry-entry {
  padding: 4px 4px 8px;
  background: #fff;
}

.message-detail__retry-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.message-detail__retry-header-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex-wrap: wrap;
}

.message-detail__retry-header-label {
  color: #303133;
  font-weight: 700;
}

.message-detail__retry-header-time {
  color: #909399;
  font-size: 12px;
}

.message-detail__retry-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}

.message-detail__retry-state-inline {
  max-width: 420px;
  overflow: hidden;
  color: #606266;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-detail__retry-state-message {
  margin: 0 0 10px;
  color: #303133;
  line-height: 1.6;
}

.message-detail__retry-message-id {
  margin-bottom: 8px;
  color: #303133;
  font-family: SFMono-Regular, Consolas, monospace;
  font-size: 13px;
  word-break: break-word;
}

.message-detail__retry-hop {
  margin-bottom: 8px;
  color: #e6a23c;
  font-family: SFMono-Regular, Consolas, monospace;
  font-size: 12px;
  word-break: break-word;
}

.message-detail__retry-details {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
}

.message-detail__retry-details div {
  padding: 10px 12px;
  border-radius: 10px;
  background: #f5f7fa;
}

.message-detail__retry-details dt {
  margin: 0 0 6px;
  color: #909399;
  font-size: 12px;
  font-weight: 600;
}

.message-detail__retry-details dd {
  margin: 0;
  color: #303133;
  word-break: break-word;
}

.message-detail__mini-empty {
  padding: 18px;
  border: 1px dashed #dcdfe6;
  border-radius: 12px;
  color: #909399;
  text-align: center;
}

.message-detail__empty {
  padding: 40px 24px;
  text-align: center;
  color: #909399;
}

.message-detail__empty i {
  font-size: 32px;
  color: #c0c4cc;
}

.message-detail__empty h3 {
  margin: 12px 0 8px;
  color: #303133;
}

.message-detail__empty p {
  margin: 0;
}

@media (max-width: 1024px) {
  .message-detail__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 767px) {
  .message-detail__meta-item {
    grid-template-columns: minmax(0, 1fr);
    gap: 6px;
  }

  .message-detail__retry-details {
    grid-template-columns: minmax(0, 1fr);
  }

  .message-detail__summary-top {
    flex-direction: column;
    align-items: flex-start;
  }

  .message-detail__retry-header {
    align-items: flex-start;
  }

  .message-detail__retry-state-inline {
    max-width: 100%;
    white-space: normal;
  }
}
</style>
